/** Interface theme (light / dark). The stored preference lives on the backend
 *  (PATCH /api/settings, sign-in required). This module resolves it — with the
 *  dark_mode gate, the sign-in state and the OS scheme — into the `.dark` class
 *  and `color-scheme` on <html>, and caches the last committed preference so
 *  the inline `theme-boot` script in src/index.html can paint dark before the
 *  bundle loads. Mirrors font-size-preference.ts (module state + listener set,
 *  preview vs. commit). Only the interface is themed: canvas objects never
 *  change color, and the canvas background is its own preference
 *  (canvas-color-preference.ts). */
import type { GateStatus } from '@/analytics/types';
import type { Theme } from '@/lib/api-client';
import { isEmbedDocument } from '@/utils/embed-document';

export type EffectiveTheme = 'light' | 'dark';

/** Read by the `theme-boot` script in src/index.html — keep the two in sync
 *  (pinned by test/theme-boot-script.test.ts). Written only while the gate is
 *  on and the user is signed in, cleared otherwise, so its presence implies
 *  both. */
export const THEME_CACHE_KEY = 'stitchpress-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export interface ThemeInputs {
    /** Loaded/committed (or previewed) preference; null until settings load. */
    preference: Theme | null;
    gate: GateStatus;
    /** null while Clerk is still loading. */
    signedIn: boolean | null;
    systemDark: boolean;
    /** Last committed preference — what the boot script painted. */
    cached: Theme | null;
}

function toEffective(theme: Theme | null, systemDark: boolean): EffectiveTheme {
    return theme === 'dark' || (theme === 'system' && systemDark) ? 'dark' : 'light';
}

/** Dark only when the gate is on, the user is signed in and the preference
 *  resolves dark. While any input is still resolving, hold the cached choice
 *  so the page never flips away from what the boot script painted. */
export function resolveEffectiveTheme(i: ThemeInputs): EffectiveTheme {
    if (i.gate === 'off' || i.signedIn === false) return 'light';
    if (i.gate === 'on' && i.signedIn === true && i.preference != null) {
        return toEffective(i.preference, i.systemDark);
    }
    return toEffective(i.cached, i.systemDark);
}

function isTheme(v: string | null): v is Theme {
    return v === 'light' || v === 'dark' || v === 'system';
}

function readCache(): Theme | null {
    try {
        const v = window.localStorage.getItem(THEME_CACHE_KEY);
        return isTheme(v) ? v : null;
    } catch {
        return null;
    }
}

function writeCache(theme: Theme): void {
    try {
        window.localStorage.setItem(THEME_CACHE_KEY, theme);
    } catch {
        // Ignore storage failures (private mode, etc.).
    }
}

function clearCache(): void {
    try {
        window.localStorage.removeItem(THEME_CACHE_KEY);
    } catch {
        // Ignore storage failures.
    }
}

function systemDark(): boolean {
    try {
        return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
    } catch {
        return false;
    }
}

type Listener = (theme: EffectiveTheme) => void;
const listeners = new Set<Listener>();

let preference: Theme | null = null;
let preview: Theme | null = null;
let gate: GateStatus = 'pending';
let signedIn: boolean | null = null;
let effective: EffectiveTheme = 'light';
let offSystem: (() => void) | null = null;

function applyToDocument(theme: EffectiveTheme): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    root.style.colorScheme = theme;
}

function sync(): void {
    if (gate === 'on' && signedIn === true && preference != null) writeCache(preference);
    else if (gate === 'off' || signedIn === false) clearCache();
    const next = resolveEffectiveTheme({
        preference: preview ?? preference,
        gate,
        signedIn,
        systemDark: systemDark(),
        cached: readCache(),
    });
    applyToDocument(next);
    if (next !== effective) {
        effective = next;
        for (const l of listeners) l(next);
    }
}

/** Call once at startup (src/index.tsx): subscribes to OS scheme changes and
 *  re-applies the cached theme through the module (matching the boot script).
 *  In the /embed/* iframe it paints light and stops there — no OS listener, no
 *  cache: ThemeController isn't mounted there, so the gate and sign-in inputs
 *  would stay unresolved and the cached (possibly dark) theme would stick. */
export function initTheme(): void {
    if (isEmbedDocument()) {
        effective = 'light';
        applyToDocument('light');
        return;
    }
    if (offSystem == null && typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        const mql = window.matchMedia(DARK_QUERY);
        const onChange = (): void => sync();
        mql.addEventListener?.('change', onChange);
        offSystem = () => mql.removeEventListener?.('change', onChange);
    }
    sync();
}

export function setThemeGate(status: GateStatus): void {
    gate = status;
    sync();
}

export function setThemeSignedIn(value: boolean): void {
    signedIn = value;
    if (!value) preference = null;
    sync();
}

/** The stored preference as loaded from the backend (ThemeController's
 *  settings fetch). Unlike commitTheme it keeps any active preview: a load
 *  landing while Settings → Appearance holds an unsaved theme must not snap
 *  the page back to the saved one. */
export function loadStoredTheme(theme: Theme): void {
    preference = theme;
    sync();
}

/** The stored preference after a successful Apply. Clears any preview. */
export function commitTheme(theme: Theme): void {
    preference = theme;
    preview = null;
    sync();
}

/** Live preview (Settings → Appearance) without caching; null clears it. */
export function applyThemePreview(theme: Theme | null): void {
    preview = theme;
    sync();
}

export function getEffectiveTheme(): EffectiveTheme {
    return effective;
}

export function onEffectiveThemeChange(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Test-only: drop all module state and the OS listener. */
export function __resetThemeForTests(): void {
    offSystem?.();
    offSystem = null;
    preference = null;
    preview = null;
    gate = 'pending';
    signedIn = null;
    effective = 'light';
    listeners.clear();
}
